// "Link existing customer" flow for an orphan call card (aid=0).
//
// A known customer phones in from a number not on file and lands on an
// unmatched card. Staff asks the caller for their postcode (then surname,
// then email), types it into the ONE search field here, and links the card
// to the matching Breeze account in a click. Linking also writes the calling
// number back so the same number auto-matches next time. No match → fall
// through to "Create new account" (the existing CreateAccountModal).

import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AccountSearchMatchField,
  AccountSearchResult,
} from "@breeze/shared";
import { linkCardToAccount, searchAccountsByQuery } from "../api.js";
import { PhoneLink } from "./PhoneLink.js";

type Props = {
  open: boolean;
  onClose: () => void;
  // The orphan card being linked.
  cardId: number;
  // Called after a successful link so the parent can refresh + close.
  onLinked: () => void;
  // "Not found? Create new account" → hand off to the existing create flow.
  onCreateNew: () => void;
};

export function LinkExistingCustomerModal({
  open,
  onClose,
  cardId,
  onLinked,
  onCreateNew,
}: Props): JSX.Element {
  const qc = useQueryClient();
  const [term, setTerm] = useState("");
  const [debounced, setDebounced] = useState("");
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset on every open.
  useEffect(() => {
    if (open) {
      setTerm("");
      setDebounced("");
      setError(null);
      setLinking(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Debounce 250ms so we don't hit the API on every keystroke.
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(term.trim()), 250);
    return () => window.clearTimeout(t);
  }, [term]);

  const hasSearch = debounced.length > 0;
  const results = useQuery<AccountSearchResult[]>({
    queryKey: ["accounts-search-single", debounced],
    queryFn: () => searchAccountsByQuery(debounced),
    enabled: open && hasSearch,
    staleTime: 30_000,
  });

  async function handleLink(r: AccountSearchResult): Promise<void> {
    setLinking(true);
    setError(null);
    try {
      await linkCardToAccount(cardId, r.aid);
      // Card moves out of orphan state live via the card.updated broadcast;
      // refresh this card's cluster so the Visits/contact panels populate.
      await qc.invalidateQueries({ queryKey: ["cluster", cardId] });
      onLinked();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "link failed");
    } finally {
      setLinking(false);
    }
  }

  return (
    <AnimatePresence>
      {open ? (
        <>
          <motion.div
            key="scrim"
            className="fixed inset-0 z-[80] bg-ink-950/80"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            key="link"
            className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto p-3 md:p-8"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
          >
            <div
              className="relative w-full max-w-3xl rounded-xl border border-ink-700 bg-ink-800 shadow-drag"
              onClick={(e) => e.stopPropagation()}
            >
              <header className="flex items-center justify-between border-b border-ink-700 px-5 py-4">
                <div>
                  <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
                    Link existing customer
                  </div>
                  <h3 className="mt-0.5 text-xl font-semibold text-ink-50">
                    Find this caller, then link the card to their account
                  </h3>
                </div>
                <button
                  onClick={onClose}
                  className="rounded-md border border-ink-500 px-3 py-1 text-[13px] text-ink-50 hover:border-ink-400"
                >
                  Close
                </button>
              </header>

              <div className="px-5 pt-4">
                <input
                  autoFocus
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  placeholder="Postcode, surname, email or phone…"
                  className="w-full rounded-md border border-ink-500 bg-ink-900 px-3 py-2 text-[14px] text-ink-50 placeholder:text-ink-300 focus:border-slack-aubergineLight focus:outline-none"
                />
                <p className="mt-1.5 text-[12px] text-ink-200">
                  Ask the caller for their postcode first, then surname, then email.
                </p>
              </div>

              <div className="px-5 py-4">
                {!hasSearch ? (
                  <EmptyState>
                    Type a postcode, surname, email or phone to find the customer.
                  </EmptyState>
                ) : results.isLoading ? (
                  <EmptyState>Searching…</EmptyState>
                ) : results.isError ? (
                  <EmptyState tone="error">
                    Search failed: {(results.error as Error).message}
                  </EmptyState>
                ) : (results.data ?? []).length === 0 ? (
                  <EmptyState>
                    No matches. Use <b>Create new account</b> below for a brand-new
                    customer.
                  </EmptyState>
                ) : (
                  <ul className="divide-y divide-ink-700 rounded-md border border-ink-700 bg-ink-900/40">
                    {(results.data ?? []).map((r) => (
                      <ResultRow
                        key={r.aid}
                        result={r}
                        disabled={linking}
                        onLink={() => void handleLink(r)}
                      />
                    ))}
                  </ul>
                )}
              </div>

              <footer className="flex items-center justify-between gap-2 border-t border-ink-700 px-5 py-3">
                <div className="text-[12px]">
                  {error ? (
                    <span className="text-slack-pink">⚠ {error}</span>
                  ) : (
                    <span className="text-ink-200">Not the right person?</span>
                  )}
                </div>
                <button
                  onClick={() => {
                    onClose();
                    onCreateNew();
                  }}
                  disabled={linking}
                  className="rounded-md border border-ink-500 px-3 py-1.5 text-[13px] font-medium text-ink-50 hover:border-ink-400 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Create new account
                </button>
              </footer>
            </div>
          </motion.div>
        </>
      ) : null}
    </AnimatePresence>
  );
}

function ResultRow({
  result: r,
  disabled,
  onLink,
}: {
  result: AccountSearchResult;
  disabled: boolean;
  onLink: () => void;
}): JSX.Element {
  return (
    <li className="flex items-center gap-3 px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span
            className={`truncate text-[14px] font-medium ${
              r.deleted ? "text-ink-200 line-through" : "text-ink-50"
            }`}
          >
            {r.contactName || `Account #${r.aid}`}
          </span>
          {r.matchedFields.map((f) => (
            <MatchBadge key={f} field={f} />
          ))}
          {r.deleted ? (
            <span className="rounded border border-slack-pink/50 bg-slack-pink/20 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-slack-pink">
              deleted
            </span>
          ) : null}
          {r.cardId !== null ? (
            <span
              className="rounded border border-ink-500 bg-ink-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-ink-200"
              title="This customer already has a card on the board — linking will point this call at the same account."
            >
              card #{r.cardId}
            </span>
          ) : null}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 truncate text-[12px] text-ink-200">
          {[r.postcode, r.email].filter(Boolean).map((piece, i, arr) => (
            <span key={`${piece}-${i}`}>
              {piece}
              {i < arr.length - 1 ? <span className="ml-2 text-ink-300">·</span> : null}
            </span>
          ))}
          {r.phone ? (
            <>
              {r.postcode || r.email ? <span className="text-ink-300">·</span> : null}
              <PhoneLink number={r.phone} />
            </>
          ) : null}
          {fmtLastVisit(r) ? (
            <>
              {r.postcode || r.email || r.phone ? (
                <span className="text-ink-300">·</span>
              ) : null}
              <span>{fmtLastVisit(r)}</span>
            </>
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <button
          onClick={onLink}
          disabled={disabled}
          className="rounded-md border border-slack-aubergineLight bg-slack-aubergine px-2.5 py-1 text-[12px] font-medium text-ink-50 hover:bg-slack-aubergineLight disabled:cursor-not-allowed disabled:opacity-50"
          title="Link this call card to this customer's account"
        >
          Link to this card
        </button>
      </div>
    </li>
  );
}

function MatchBadge({ field }: { field: AccountSearchMatchField }): JSX.Element {
  const label = field.charAt(0).toUpperCase() + field.slice(1);
  return (
    <span className="rounded bg-slack-aubergine/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-ink-50">
      {label}
    </span>
  );
}

function fmtLastVisit(r: AccountSearchResult): string {
  if (r.visitCount === 0) return "no visits";
  if (!r.lastVisitDate) return `${r.visitCount} visit${r.visitCount === 1 ? "" : "s"}`;
  return `${r.visitCount} visit${r.visitCount === 1 ? "" : "s"}, last ${r.lastVisitDate}`;
}

function EmptyState({
  children,
  tone = "default",
}: {
  children: React.ReactNode;
  tone?: "default" | "error";
}): JSX.Element {
  const cls =
    tone === "error"
      ? "border-slack-pink/40 text-slack-pink"
      : "border-dashed border-ink-500 text-ink-200";
  return (
    <div className={`rounded-md border bg-ink-900/40 px-4 py-6 text-center text-[13px] ${cls}`}>
      {children}
    </div>
  );
}
